import React, {
  ReactElement, Ref, useEffect, useRef, useState,
} from 'react';
import {
  ActivityIndicator, Dimensions, Linking, StyleSheet, Text, TextInput, View, FlatList, Pressable,
  RefreshControl, ScrollView,
} from 'react-native';
import * as Animatable from 'react-native-animatable';
import Moment from 'moment';
import { useSelector } from 'react-redux';
import SearchIcon from '../components/SearchIcon';
import Colors from '../constants/Colors';
import getAssets from '../requests/Assets/GetAssets';
import getTemplateGroups from '../requests/Assets/GetTemplateGroups';
import { RootTabScreenProps } from '../types';
import AssetRow from '../components/AssetManagement/AssetRow';
import EllipsisIcon from '../components/EllipsisIcon';
import getTemplateSpreadsheet from '../requests/Assets/GetTemplateSpreadsheet';
import OrionArrowIcon from '../components/24pxIcons/OrionArrowIcon';
import CheckBox from '../components/16pxIcons/CheckBox';
import PlusIcon from '../components/16pxIcons/PlusIcon';
import TemplateRowWeb from '../components/AssetManagement/TemplateRowWeb';
import AddTemplateGroupModal from '../components/AssetManagement/AddTemplateGroupModal';
import TemplateSideBarRow from '../components/AssetManagement/TemplateSideBarRow';
import FilterIcon16 from '../components/16pxIcons/FilterIcon';
import AlertModal from '../components/AlertModal';
import SearchResultsPane from '../components/AssetManagement/SearchResultsPane';
import BackIconSmall from '../components/12pxIcons/BackIconSmall';
import AddTemplateModal from '../components/AssetManagement/AddTemplateModal';
import ScreenEmptyState from '../components/ScreenEmptyState';
import NoTemplateGroupsImage from '../components/AssetManagement/NoTemplateGroupsImage';
import NoTemplatesImage from '../components/AssetManagement/NoTemplatesImage';
import NoAssetsImage from '../components/AssetManagement/NoAssetsImage';
import { logEvent } from '../components/LogEvent';
import AssetFilter from '../components/AssetManagement/AssetFilter';
import EditIcon24 from '../components/24pxIcons/EditIcon24';
import Style from '../constants/Style';
import PressableStyled, { PressableState } from '../components/PressableStyled';
import {
  AssetInstance, Template, TemplateGroup, UserGroup, UserPermission,
} from '../constants/Types';
import getUserGroups from '../requests/Users/GetUserGroups';
import { ATLEAST_ONE_USER_GROUP } from '../constants/AlertMessages';
import ExportButton from '../components/ExportButton';
import { AppScreen } from '../navigation/LinkingConfiguration';

export default function TemplateMgmtScreen({
  navigation, route,
}: RootTabScreenProps<'AssetManagement'>) {
  const [assetsLoading, setAssetsLoading] = useState<boolean>(true);
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(true);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [userGroupsLoaded, setUserGroupsLoaded] = useState<boolean>(false);
  const [addTemplateModalVisible, setAddTemplateModalVisible] = useState<boolean>(false);
  const [linkAssetsView, setLinkAssetsView] = useState<boolean>(false);
  const [selectedTitle, setSelectedTitle] = useState<string>('');
  const [templateGroups, setTemplateGroups] = useState([]);
  const [templates, setTemplates] = useState<Template[]>(null);
  const [assets, setAssets] = useState<AssetInstance[]>([]);
  const [exportDropdownShown, setExportDropdownShown] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [allAssets, setAllAssets] = useState<AssetInstance[]>([]);
  const [selectedGroupID, setSelectedGroupID] = useState<string>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template>(null);
  const [selectedAsset, setSelectedAsset] = useState<AssetInstance>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string>(null);
  const [allAssetsCount, setAllAssetsCount] = useState<number>(0);
  const [assetCount, setAssetCount] = useState<number>(0);
  const [allSelected, setAllSelected] = useState(null);
  const [sortById, setSortById] = useState<boolean>(true);
  const [sortByFloor, setSortByFloor] = useState(null);
  const [sortByArea, setSortByArea] = useState(null);
  const [detailedFilterVisible, setDetailedFilterVisible] = useState<boolean>(false);
  const [assetFilterLoading, setAssetFilterLoading] = useState<boolean>(false);
  const [assetFilterResults, setAssetFilterResults] = useState([]);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [addGroupModalVisible, setAddGroupModalVisible] = useState<boolean>(false);
  const [templateSideBarContext, setTemplateSideBarContext] = useState<string>(null);
  const [searchResultsPaneFocused, setSearchResultsPaneFocused] = useState<boolean>(false);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const buildings: object = useSelector((state:
    { locations: { buildings } }) => state.locations.buildings);
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canAddTemplates: boolean = isAdmin
    || permissions.includes(UserPermission.TEMPLATES_CREATE);
  const canDeleteTemplates: boolean = isAdmin
    || permissions.includes(UserPermission.TEMPLATES_DELETE);
  const canUpdateTemplates: boolean = isAdmin
    || permissions.includes(UserPermission.TEMPLATES_UPDATE);
  const assetSearchField = useRef<TextInput>(null);
  const templateSearchField = useRef<TextInput>(null);
  const templateFlatlist: Ref<FlatList> = useRef<FlatList>(null);
  const templateGroupList = useRef<FlatList>(null);

  const showCheckbox = false;

  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [noDataAlertShown, setNoDataAlertShown] = useState<boolean>(false);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);

  const showScreenAlert = (title: string, message?: string, error: boolean = true) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertVisible(true);
  };

  useEffect(() => {
    getUserGroups().then((response: { UserGroups?: UserGroup[] }) => {
      setUserGroups(response?.UserGroups ?? []);
      setUserGroupsLoaded(true);
    });
    const unsubscribe = navigation.addListener('blur', hideMenus);
    return unsubscribe;
  }, []);

  const hideMenus: () => void = () => {
    setTemplateSideBarContext(null);
    setDetailedFilterVisible(false);
    setExportDropdownShown(false);
  };

  const exportError = (errorText: string) => {
    setExporting(false);
    showScreenAlert('Error', errorText ?? 'Unable to export data at the moment. Please try again later.');
  };

  const exportData = (buildingId?: string) => {
    setExporting(true);
    setExportDropdownShown(false);
    getTemplateSpreadsheet(buildingId).then((response) => {
      if (response && response.url?.length > 0) {
        Linking.openURL(response.url);
      } else exportError(response?.errorText);
      setExporting(false);
    }).catch((error) => exportError(error?.message));
  };

  useEffect(() => {
    setSearchValue('');
  }, [selectedTemplate, selectedGroupID]);

  useEffect(() => {
    setTemplateSideBarContext(null);
  }, [
    addTemplateModalVisible, selectedTemplate, selectedGroupID, searchValue,
    addGroupModalVisible,
  ]);

  const noTemplatesFound = () => {
    if (!noDataAlertShown) {
      setTemplates([]);
      showScreenAlert('Error', 'Could not load asset templates at the moment. Please try again later.');
      setNoDataAlertShown(true);
      setTemplatesLoading(false);
    }
  };

  const noAssetsFound = () => {
    if (!noDataAlertShown) {
      showScreenAlert('Error', 'Could not load assets at the moment. Please try again later');
      setNoDataAlertShown(true);
      setAssetsLoading(false);
    }
  };

  useEffect(() => {
    getTemplateGroups().then((response) => {
      if (!response?.groups) noTemplatesFound();
      else {
        setTemplateGroups(response?.groups?.sort((a: TemplateGroup, b: TemplateGroup) => a.Name
          ?.toLowerCase().localeCompare(b.Name?.toLowerCase())) ?? []);
        const templateDataList: Template[] = response?.groups?.reduce((prev, group) => {
          if (group?.buttonPrototypes?.length) return prev.concat(group.buttonPrototypes);
          return prev;
        }, []) ?? [];
        setTemplates(templateDataList);
      }
    }).catch(noTemplatesFound);
  }, []);

  useEffect(() => {
    setSelectedAsset(null);
    loadAssets();
    if (selectedTemplate) {
      templates?.forEach((template, index) => {
        if (template.id === selectedTemplate.id) {
          templateFlatlist?.current?.scrollToIndex({ index, viewPosition: 0.5 });
        }
      });
    }
  }, [selectedTemplate, selectedBuildingId]);

  useEffect(() => {
    if (selectedGroupID) {
      templateGroups?.sort((a: TemplateGroup, b: TemplateGroup) => a.Name
        ?.toLowerCase().localeCompare(b.Name?.toLowerCase()))
        ?.forEach((templateGroup, index) => {
          if (templateGroup.ButtonPrototypeGroupId === selectedGroupID) {
            templateGroupList?.current?.scrollToIndex({ index, viewPosition: 0.5 });
          }
        });
    }
  }, [selectedGroupID]);

  useEffect(() => {
    if (templatesLoading && templates) {
      setTemplatesLoading(false);
      setInitialLoad(false);
    }
  }, [templates]);

  const clearAssetFilter = () => {
    setAssetFilterResults([]);
    setDetailedFilterVisible(false);
  };

  const clearTabSelection = () => {
    setSelectedTitle('');
    setSelectedTemplate(null);
    setAssets([]);
    setSelectedAsset(null);
    setSelectedGroupID(null);
    hideMenus();
    clearAssetFilter();
    setAllSelected(false);
  };

  useEffect(() => {
    getAssets({ locationId: selectedBuildingId, enableSubLocationsLookUp: true })
      .then((res) => {
        setAllAssets(res.assets);
        setAllAssetsCount(res?.count);
      });
  }, []);

  const loadAssets = () => {
    if (selectedTemplate !== null) {
      getAssets({ templateId: selectedTemplate?.id, locationId: selectedBuildingId })
        .then((response) => {
          if (response) {
            setAssetCount(response.count);
            setAssets(response.assets);
            setAssetsLoading(false);
          } else noAssetsFound();
        }).catch(noAssetsFound);
    } else if (!(route?.params as any)?.assetId) {
      setAssetsLoading(false);
    }
  };

  const refresh = () => {
    setAssetsLoading(true);
    clearTabSelection();
    setAssets([]);
    loadAssets();
  };

  const deleteAsset = (assetId: string) => {
    let updatedAssets: AssetInstance[] = [...assets];
    updatedAssets = updatedAssets.filter((asset: AssetInstance) => asset.id !== assetId);
    setAssets(updatedAssets);
    setAssetFilterResults(assetFilterResults?.filter((asset: AssetInstance) => asset.id
      !== assetId));
    if (selectedAsset?.id === assetId) setSelectedAsset(null);
  };

  const updateAsset = (updatedAsset: AssetInstance) => {
    setAssets([...assets.map((asset: AssetInstance) => ({
      ...asset, ...(asset.id === updatedAsset.id ? updatedAsset : {}),
    }))]);
    if (selectedAsset?.id === updatedAsset.id) {
      setSelectedAsset({ ...selectedAsset, ...updatedAsset });
    }
    setAssetFilterResults(assetFilterResults?.map((asset: AssetInstance) => (asset.id
      === updatedAsset.id ? updatedAsset : asset)));
  };

  useEffect(() => {
    if (!(templates && !templatesLoading)) return;
    const assetId: string = (route?.params as any)?.assetId;
    const templateId: string = (route?.params as any)?.templateId;

    if (assetId || templateId) {
      if (selectedAsset?.id === assetId) return;
      if (templateId && selectedTemplate?.id !== templateId) {
        const filteredTemplate: Template | null = templates
          .filter((template: Template) => template.id === templateId)[0];
        if (filteredTemplate) {
          setSelectedTemplate(filteredTemplate);
          setAssets([]);
          setAssetsLoading(true);
          setSelectedTitle(filteredTemplate.name);
          setLinkAssetsView(true);
        } else {
          setSelectedTemplate(null);
          showScreenAlert('Error', 'Linked template could not be found. It may have been deleted.');
        }
      }
      if (assetId) setSelectedAssetId(assetId);

      navigation.setParams({ templateId: undefined, assetId: undefined } as any);
    }
  }, [route, templates, templatesLoading]);

  useEffect(() => {
    if (!(selectedAssetId && selectedTemplate && !assetsLoading)) return;
    const filteredAsset = assets
      ?.filter((asset: AssetInstance) => asset.id === selectedAssetId)[0];
    if (filteredAsset) setSelectedAsset(filteredAsset);
    else {
      showScreenAlert('Error', 'Linked asset could not be found. It may have been deleted.');
    }
    setSelectedAssetId(null);
  }, [selectedTemplate, selectedAssetId, assetsLoading]);

  const updateTemplateLocally = (template: Template, add: boolean, groupID: string) => {
    if (!template?.id) return;
    setTemplatesLoading(true);
    if (add) {
      setTemplateGroups(templateGroups.map((group: TemplateGroup) => ({
        ...group,
        buttonPrototypes: group.ButtonPrototypeGroupId === groupID
          ? [...group.buttonPrototypes, template] : group.buttonPrototypes,
      })));
      setTemplates([...templates, template]);
    } else {
      setTemplateGroups(templateGroups.map((group: TemplateGroup) => ({
        ...group,
        buttonPrototypes: group.buttonPrototypes.map((t: Template) => ({
          ...t,
          ...(t.id === template.id ? template : {}),
        })),
      })));
      setTemplates(templates
        .map((t: Template) => ({ ...t, ...(t.id === template.id ? template : {}) })));
    }
    if (selectedTemplate && selectedTemplate.id === template.id) {
      setSelectedTemplate(template);
      loadAssets();
    }
  };

  if (initialLoad || !userGroupsLoaded) {
    return (
      <View style={{ flex: 1 }}>
        <View style={{ flex: 1, justifyContent: 'center' }}>
          <ActivityIndicator size={'large'} color={Colors.light.hereworksLoading} />
        </View>
      </View>
    );
  }

  const filterAssets = () => {
    let assetsToRender = [...assets];
    if (sortByFloor === true) {
      // eslint-disable-next-line max-len
      assetsToRender.sort((a, b) => a.locationFloor?.localeCompare(b?.locationFloor));
    } else if (sortByFloor === false) {
      // eslint-disable-next-line max-len
      assetsToRender.sort((a, b) => b.locationFloor?.localeCompare(a?.locationFloor));
    } else if (sortByArea === true) {
      assetsToRender.sort((a, b) => a.locationArea?.localeCompare(b?.locationArea));
    } else if (sortByArea === false) {
      assetsToRender.sort((a, b) => b.locationArea?.localeCompare(a?.locationArea));
    } else if (sortById === true) {
      // eslint-disable-next-line max-len
      assetsToRender.sort((a, b) => a.name?.localeCompare(b.name));
    } else if (sortById === false) {
      // eslint-disable-next-line max-len
      assetsToRender.sort((a, b) => b.name?.localeCompare(a.name));
    }
    if (selectedTemplate) {
      assetsToRender = assetsToRender
        ?.filter((asset: AssetInstance) => asset?.templateId === selectedTemplate.id);
    }
    return assetsToRender ?? [];
  };

  const renderTemplate = (template: any) => {
    const selected: boolean = selectedTemplate?.id === template.id && !assetFilterResults?.length;
    return (
      <Pressable
        onPress={() => {
          logEvent('AssetManagementWeb', 'selected_template');
          setAssetsLoading(true);
          setSelectedTemplate(selectedTemplate?.id === template.id ? null : template);
          setSelectedAsset(null);
          setAllSelected(false);
          setSelectedTitle(selectedTemplate?.id === template.id ? null : template.name);
          clearAssetFilter();
        }}
        style={(state) => [{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: selected ? Colors.light.hereworksDarkBlue : 'transparent',
        }, state.hovered && Style.hovered, state.pressed && Style.pressed]}
      >
        <Text
          style={[
            styles.sectionTitle,
            { color: selected ? '#fff' : Colors.light.hereworksBlack500 },
          ]}
        >
          {template.name}
        </Text>
        {!linkAssetsView && (
          <EllipsisIcon fill={'#D9D9D9'} style={{ position: 'absolute', right: 18 }} />
        )}
      </Pressable>
    );
  };

  const renderTemplatesFromList = (templatesList) => <FlatList
    data={templatesList
      ?.sort((a: Template, b: Template) => a.name?.toLowerCase()
        .localeCompare(b.name?.toLowerCase()))}
    keyExtractor={(template) => `template_${template.id}`}
    renderItem={({ item: template }) => !template.deleted && <TemplateRowWeb
      {...{ templateGroups, template }}
      closeMenu={() => setTemplateSideBarContext(null)}
      showParentAlert={showScreenAlert}
      update={updateTemplateLocally}
      setLinkAssetsView={() => {
        setLinkAssetsView(!linkAssetsView);
        clearAssetFilter();
        setAssetsLoading(true);
        setSelectedTemplate(template);
        setSelectedAsset(null);
        setAllSelected(false);
      }}
      setSelectedId={() => { }}
      setSelectedTitle={() => setSelectedTitle(template.name)}
      deleteAction={(id: string) => {
        const updatedTemplateGroups = [...templateGroups];
        updatedTemplateGroups.forEach((group) => {
          const updatedTemplates = (group.buttonPrototypes ?? [])
            .filter((currentTemplate) => currentTemplate.id !== id);
          group.buttonPrototypes = updatedTemplates;
        });
        setTemplateGroups(updatedTemplateGroups);
        const newArray = templates.filter((t: Template) => t.id !== id);
        setTemplates(newArray);
      }}
    />}
    testID='template-list'
  />;

  const renderTemplates = () => {
    if (selectedGroupID === null) return renderTemplatesFromList(templates);
    return renderTemplatesFromList(
      templateGroups?.filter((group) => group.ButtonPrototypeGroupId === selectedGroupID)[0]
        ?.buttonPrototypes ?? templates,
    );
  };

  const renderSearchPane = () => (searchValue?.length ? (
    <SearchResultsPane
      focused={searchResultsPaneFocused}
      blur={() => {
        (linkAssetsView ? assetSearchField : templateSearchField)?.current?.focus();
      }}
      {...{
        searchValue, templates, templateGroups, selectedAsset, selectedTemplate, setSearchValue,
      }}
      assets={allAssets}
      selectTemplate={setSelectedTemplate}
      selectAsset={setSelectedAsset}
      selectTemplateGroup={setSelectedGroupID}
      showAssets={setLinkAssetsView}
      selectedTemplateGroup={selectedGroupID}
      assetsShown={linkAssetsView}
    />
  ) : null);

  const renderManageGroupsView = () => (
    <Animatable.View
      animation={'fadeIn'}
      duration={300}
      style={{
        borderRadius: 24,
        marginVertical: 20,
        overflow: 'hidden',
        flexDirection: 'row',
        alignSelf: 'stretch',
        backgroundColor: '#fff',
        flex: 1,
      }}
    >
      <View
        style={{
          minWidth: 240,
          flex: 2,
          backgroundColor: Colors.light.hereworksBlue50,
        }}
      >
        <View
          style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Text style={styles.templateGroupsTitle}>
            {'TEMPLATE GROUPS'}
          </Text>
          {canAddTemplates && <Pressable
            style={(state) => [
              styles.addTemplateGroupsButton,
              state.hovered && Style.hovered, state.pressed && Style.pressed,
              state.hovered && { backgroundColor: Colors.light.hereworksBlue100 },
            ]}
            onPress={() => {
              logEvent('AssetManagementWeb', 'Add_Template_Group');
              setAddGroupModalVisible(!addGroupModalVisible);
            }}
            testID='am-add-template-group-btn'
          >
            <PlusIcon color={Colors.light.hereworksBlue500} />
          </Pressable>}
          <AddTemplateGroupModal
            {...{ templateGroups }}
            isVisible={addGroupModalVisible}
            hide={() => setAddGroupModalVisible(false)}
            update={(Name: string, ButtonPrototypeGroupId: string, create: boolean) => {
              const updatedTemplateGroups = [...templateGroups];
              if (create) {
                updatedTemplateGroups.push({
                  Name,
                  ButtonPrototypeGroupId,
                  buttonPrototypes: [],
                });
              } else {
                updatedTemplateGroups?.forEach((group) => {
                  if (group.ButtonPrototypeGroupId === ButtonPrototypeGroupId) {
                    group.Name = Name;
                  }
                });
              }
              setTemplateGroups(updatedTemplateGroups
                ?.sort((a: TemplateGroup, b: TemplateGroup) => a.Name
                  ?.toLowerCase().localeCompare(b.Name?.toLowerCase())) ?? []);
            }}
          />
          <AddTemplateModal
            {...{ templateGroups }}
            defaultTemplateGroupID={selectedGroupID}
            isVisible={addTemplateModalVisible}
            hide={() => setAddTemplateModalVisible(false)}
            update={updateTemplateLocally}
            showParentAlert={showScreenAlert}
          />
        </View>
        <View
          style={{ backgroundColor: Colors.light.hereworksBlue100, height: 1, width: '100%' }}
        />
        <FlatList
          ref={(ref) => {
            templateGroupList.current = ref;
          }}
          onScrollToIndexFailed={() => { }}
          data={templateGroups?.sort((prev, next) => next.Name?.toLowerCase()
            .localeCompare(prev.Name?.toLowerCase()))}
          contentContainerStyle={{ flexDirection: 'column-reverse' }}
          keyExtractor={(item) => `template_group_menu_item_${item.ButtonPrototypeGroupId}`}
          renderItem={({ item }) => <TemplateSideBarRow
            {...{
              selectedGroupID, templateGroups, showScreenAlert,
            }}
            group={item}
            onPress={() => {
              if (selectedGroupID === item.ButtonPrototypeGroupId) {
                setSelectedGroupID(null);
              } else {
                setSelectedGroupID(item.ButtonPrototypeGroupId);
              }
            }}
            deleteAction={() => {
              if (item.ButtonPrototypeGroupId === selectedGroupID) {
                setSelectedGroupID(null);
              }
              setTemplateGroups(templateGroups?.filter(
                (i) => i.ButtonPrototypeGroupId !== item.ButtonPrototypeGroupId,
              )?.sort((a: TemplateGroup, b: TemplateGroup) => a.Name
                ?.toLowerCase().localeCompare(b.Name?.toLowerCase())) ?? []);
            }}
            contextMenuOpen={() => setTemplateSideBarContext(item.ButtonPrototypeGroupId)}
            contextMenuClose={() => setTemplateSideBarContext(null)}
            currentContextMenu={templateSideBarContext}
            update={(newName: string, id: string, create?: boolean, toDelete?: boolean) => {
              if (create) {
                setTemplateGroups([
                  ...templateGroups, { Name: newName, ButtonPrototypeGroupId: id },
                ]?.sort((a: TemplateGroup, b: TemplateGroup) => a.Name
                  ?.toLowerCase().localeCompare(b.Name?.toLowerCase())) ?? []);
              } else if (toDelete) {
                setTemplateGroups(templateGroups?.filter((group: TemplateGroup) => group
                  .ButtonPrototypeGroupId !== id)
                  ?.sort((a: TemplateGroup, b: TemplateGroup) => a.Name
                    ?.toLowerCase().localeCompare(b.Name?.toLowerCase())) ?? []);
              } else {
                setTemplateGroups(templateGroups?.map((group: TemplateGroup) => (group
                  .ButtonPrototypeGroupId === id ? { ...group, Name: newName } : group))
                  ?.sort((a: TemplateGroup, b: TemplateGroup) => a.Name
                    ?.toLowerCase().localeCompare(b.Name?.toLowerCase())) ?? []);
              }
            }}
          />}
          ListEmptyComponent={() => <View style={styles.noTemplateGroupsMessage}>
            <Text style={styles.noTemplatesSubtitle}>
              {'No groups'}
            </Text>
          </View>}
        />
      </View>
      <View style={{ flex: 7 }}>
        <View style={styles.contentHeader}>
          <View style={styles.searchBox}>
            <SearchIcon style={{ marginStart: 14 }} />
            <TextInput
              placeholder="Search"
              value={searchValue}
              style={styles.searchText}
              ref={(ref) => {
                templateSearchField.current = ref;
              }}
              onFocus={() => setSearchResultsPaneFocused(false)}
              onChangeText={(text) => {
                setSearchValue(text);
                setSearchResultsPaneFocused(false);
              }}
              onKeyPress={(e) => {
                // @ts-ignore
                if (e.code === 'ArrowDown') setSearchResultsPaneFocused(true);
              }}
            />
          </View>
          {canAddTemplates && <PressableStyled
            style={styles.createButton}
            onPress={() => {
              logEvent('AssetManagementWeb', 'Create_new_template');
              if (!userGroups.length) {
                showScreenAlert(
                  'No user group found',
                  ATLEAST_ONE_USER_GROUP,
                  true,
                );
              } else setAddTemplateModalVisible(true);
            }}
          >
            <Text style={styles.createButtonTitle}>Add Template</Text>
            <PlusIcon />
          </PressableStyled>}
        </View>
        {renderSearchPane()}
        <ScrollView
          style={{
            flex: 1,
          }}
        >
          <View style={{}}>
            <Text
              style={{
                fontFamily: 'Poppins_400Regular',
                fontSize: 22,
                color: Colors.light.hereworksBlack600,
                marginVertical: 24,
                marginStart: 24,
              }}
            >
              {templateGroups[selectedGroupID]}
            </Text>
            <View style={{ marginHorizontal: 24 }}>
              <View style={{ flexDirection: 'row', alignSelf: 'stretch' }}>
                <View style={{ flex: 1, alignItems: 'flex-start' }}>
                  <Text style={styles.headerText}>{'TEMPLATES'}</Text>
                </View>
                {(canAddTemplates || canDeleteTemplates)
                  && <View style={{ flex: 1, alignItems: 'flex-end' }}>
                    <Text style={styles.headerText}>{'ACTIONS'}</Text>
                  </View>}
              </View>
              <View style={[styles.divider, { marginTop: 8, marginBottom: 16 }]} />
            </View>
            {(templatesLoading || assetsLoading)
              && <View style={{ flex: 1 }}>
                <View style={{ flex: 1, justifyContent: 'center' }}>
                  <ActivityIndicator size={'large'} color={Colors.light.hereworksLoading} />
                </View>
              </View>
            }
            {renderTemplates()}
          </View>
          {!templatesLoading && templateGroups?.length === 0 && <ScreenEmptyState
            image={<NoTemplateGroupsImage />}
            title='Start with a template group'
            subtitle={'In template groups you will collect your templates.\nContact your system administrator to create a template group'}
            adminSubtitle={'In template groups you will collect your templates.\nPlease create a template group'}
            actionAvailable={canAddTemplates}
            buttonTitle='Create template group'
            buttonAction={() => setAddGroupModalVisible(true)}
          />}
          {!templatesLoading && templateGroups?.length > 0
            && !((selectedGroupID ? templateGroups.filter((group) => group.ButtonPrototypeGroupId
              === selectedGroupID)[0]?.buttonPrototypes : templates)?.length > 0)
            && <ScreenEmptyState
              image={<NoTemplatesImage />}
              title={selectedGroupID ? 'No templates in this template group' : 'Add your first template'}
              subtitle='Contact your system administrator to add a template to work with'
              adminSubtitle='Add a template to work with'
              actionAvailable={canAddTemplates}
              buttonTitle='Add template'
              buttonAction={() => {
                logEvent('AssetManagementWeb', 'add_template_button');
                if (!userGroups.length) {
                  showScreenAlert(
                    'No user group found',
                    ATLEAST_ONE_USER_GROUP,
                    true,
                  );
                } else setAddTemplateModalVisible(true);
              }}
            />}
        </ScrollView>
      </View>
    </Animatable.View>
  );

  const renderItem = ({ item }, cardOpen: boolean = false) => <AssetRow
    key={`asset_${item.id}`}
    {...{ updateAsset, deleteAsset, cardOpen }}
    showAlert={showScreenAlert}
    setLoading={setAssetsLoading}
    asset={item}
    selected={allSelected ?? null}
    buildingId={selectedBuildingId}
    template={(assetFilterResults.length || selectedAsset)
      ? templates?.filter((template: Template) => item.templateId === template.id)[0]
      : selectedTemplate}
  />;

  return (
    <Pressable
      onPress={hideMenus}
      style={{ flex: 1, cursor: 'auto' }}
    >
      <View style={styles.container}>
        <View style={{ marginTop: 30, flexDirection: 'row', zIndex: 2 }}>
          <View style={{ flex: 1, flexDirection: 'row' }}>
            <PressableStyled
              onPress={() => {
                logEvent('AssetManagementWeb', 'templates_tab');
                clearTabSelection();
                setLinkAssetsView(false);
              }}
              testID='template-tab-btn'
            >
              <Text
                style={{
                  fontSize: 22,
                  lineHeight: 33,
                  fontFamily: 'Poppins_400Regular',
                  color: !linkAssetsView
                    ? Colors.light.hereworksBlack600
                    : Colors.light.hereworksBlack300,
                }}
              >
                {'Templates'}
              </Text>
              {!linkAssetsView && (
                <View style={styles.tabSelectionHighlight} />
              )}
            </PressableStyled>
            <PressableStyled
              style={{ marginStart: 24 }}
              onPress={() => {
                logEvent('AssetManagementWeb', 'assets_tab');
                clearTabSelection();
                setLinkAssetsView(true);
              }}
              testID='asset-tab-btn'
            >
              <Text
                style={{
                  fontSize: 22,
                  fontFamily: 'Poppins_400Regular',
                  color: !linkAssetsView
                    ? Colors.light.hereworksBlack300
                    : Colors.light.hereworksBlack600,
                }}
              >
                {'Assets'}
              </Text>
              {linkAssetsView && (
                <View style={styles.tabSelectionHighlight} />
              )}
            </PressableStyled>
          </View>
          <>
            <ExportButton
              onPress={() => {
                setTemplateSideBarContext(null);
                setDetailedFilterVisible(false);
                logEvent('AssetManagementWeb', 'Export');
                if (Object.values(buildings)?.length > 0) {
                  setExportDropdownShown(!exportDropdownShown);
                } else exportData();
              }}
              {...{ exporting }}
            />
            {exportDropdownShown && <View style={styles.exportDropdown}>
              <Pressable
                onPress={() => exportData(selectedBuildingId)}
                style={(state: { hovered: boolean, pressed: boolean }) => [
                  state.hovered && Style.hovered, state.pressed && Style.pressed,
                ]}
              >
                <Text style={styles.exportOptionText}>{'Export data for current building'}</Text>
              </Pressable>
              <Pressable
                onPress={() => exportData()}
                style={(state: { hovered: boolean, pressed: boolean }) => [
                  state.hovered && Style.hovered, state.pressed && Style.pressed,
                ]}
              >
                <Text style={styles.exportOptionText}>{'Export data for all buildings'}</Text>
              </Pressable>
            </View>}
          </>
        </View>
        {!linkAssetsView && renderManageGroupsView()}
        {linkAssetsView && (
          <Animatable.View
            animation={'fadeIn'}
            duration={300}
            style={{
              borderRadius: 24,
              marginVertical: 20,
              overflow: 'hidden',
              flexDirection: 'row',
              alignSelf: 'stretch',
              backgroundColor: '#fff',
              flex: 1,
            }}
          >
            <View
              style={{
                minWidth: 240,
                backgroundColor: Colors.light.hereworksBlue50,
                flex: 1,
              }}
            >
              <Text
                style={{
                  fontFamily: 'Roboto_500Medium',
                  fontSize: 12,
                  lineHeight: 20,
                  color: Colors.light.hereworksBlack500,
                  marginTop: 24,
                  marginStart: 24,
                  marginBottom: 17,
                }}
              >
                {'BY TEMPLATE'}
              </Text>
              <View style={{ zIndex: -1, flex: 1 }}>
                <FlatList
                  data={templates
                    ?.sort((prev: Template, next: Template) => prev.name.localeCompare(next.name))}
                  ref={templateFlatlist}
                  onScrollToIndexFailed={() => { }}
                  renderItem={({ item }) => <View
                    style={{ zIndex: -1 }}
                    key={`template_list_item_${item.id}`}
                  >
                    {renderTemplate(item)}
                  </View>}
                />
              </View>
            </View>
            <View
              style={{
                flex: 7,
              }}
            >
              <View style={styles.contentHeader}>
                {selectedAsset && (
                  <View style={styles.contentHeaderButtonSection}>
                    <Pressable
                      style={(state) => [
                        styles.backButton,
                        state.hovered && Style.hovered, state.pressed && Style.pressed,
                      ]}
                      onPress={() => {
                        logEvent('AssetManagementWeb', 'asset_back');
                        if (assetFilterResults?.length) clearAssetFilter();
                        else if (selectedAsset) setSelectedAsset(null);
                        else if (selectedTemplate) {
                          setSelectedTemplate(null);
                          setSelectedAsset(null);
                          setSelectedTitle(null);
                        }
                      }}
                    >
                      <BackIconSmall />
                    </Pressable>
                    <Text style={styles.contentHeaderText}>{'ASSETS'}</Text>
                  </View>
                )}
                <View style={styles.searchBox}>
                  <SearchIcon style={{ marginStart: 14 }} />
                  <TextInput
                    placeholder="Search"
                    value={searchValue}
                    style={styles.searchText}
                    onFocus={() => setSearchResultsPaneFocused(false)}
                    ref={(ref) => {
                      assetSearchField.current = ref;
                    }}
                    onChangeText={(text) => {
                      setSearchResultsPaneFocused(false);
                      setSearchValue(text);
                    }}
                    onKeyPress={(e) => {
                      // @ts-ignore
                      if (e.code === 'ArrowDown') setSearchResultsPaneFocused(true);
                    }}
                  />
                </View>
              </View>
              {renderSearchPane()}
              <View style={{ height: '100%' }}>
                {!selectedAsset && (
                  <View style={{ marginHorizontal: 24 }}>
                    <PressableStyled
                      style={styles.filterButton}
                      onPress={() => {
                        logEvent('AssetManagementWeb', 'Filter_button');
                        setDetailedFilterVisible(!detailedFilterVisible);
                      }}
                      testID='filter-asset-btn'
                    >
                      <Text style={styles.filterButtonTitle}>{'Filter'}</Text>
                      <FilterIcon16 />
                    </PressableStyled>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 28 }}>
                      <Text style={styles.selectedTitle} selectable>
                        {assetFilterResults?.length ? 'Filter results' : selectedTitle}
                      </Text>
                      {canUpdateTemplates && !!selectedTemplate && <Pressable
                        style={{ marginHorizontal: 8 }}
                        onPress={() => {
                          logEvent('AssetRow', 'edit_template');
                          setEditModalVisible(true);
                        }}
                        testID='am-edit-template-btn'
                      >
                        {({ hovered }: PressableState): ReactElement => (
                          <View
                            style={{
                              backgroundColor: hovered
                                ? Colors.light.hereworksBlue500
                                : 'transparent',
                              borderRadius: 4,
                            }}
                          >
                            <EditIcon24 color={hovered ? '#fff' : null} />
                          </View>
                        )}
                      </Pressable>}
                      <View
                        style={{
                          flex: 1,
                          justifyContent: 'flex-end',
                          flexDirection: 'row',
                          marginEnd: 24,
                        }}
                      >
                        <View style={{ marginStart: 32, alignItems: 'center' }}>
                          <Text style={styles.headingSubTitle}>{'ASSETS'}</Text>
                          <Text style={styles.headingValue}>
                            {assetFilterResults?.length
                              || (selectedTemplate ? assetCount : allAssetsCount)}
                          </Text>
                        </View>
                        {selectedTemplate && !assetFilterResults?.length ? (
                          <>
                            {selectedTemplate?.CreatedOn ? (
                              <View style={{ marginStart: 32, alignItems: 'center' }}>
                                <Text style={styles.headingSubTitle}>{'CREATED ON'}</Text>
                                <Text style={styles.headingValue}>
                                  {Moment(selectedTemplate?.CreatedOn).format('MMMM DD[, ]YYYY')}
                                </Text>
                              </View>
                            ) : null}
                            {selectedTemplate?.ModifiedOn ? (
                              <View style={{ marginStart: 32, alignItems: 'center' }}>
                                <Text style={styles.headingSubTitle}>{'CHANGED ON'}</Text>
                                <Text style={styles.headingValue}>
                                  {Moment(selectedTemplate?.ModifiedOn).format('MMMM DD[, ]YYYY')}
                                </Text>
                              </View>
                            ) : null}
                          </>
                        ) : null}
                      </View>
                    </View>
                    <View>
                      <View
                        style={{
                          flexDirection: 'row',
                          marginVertical: 12,
                          alignSelf: 'stretch',
                          alignItems: 'center',
                        }}
                      >
                        {showCheckbox && (
                          <Pressable
                            onPress={() => {
                              logEvent('AssetMgmtScreenWeb', 'all_selected');
                              setAllSelected(!allSelected);
                            }}
                            style={(state) => [
                              state.hovered && Style.hovered, state.pressed && Style.pressed]}
                          >
                            <CheckBox
                              checkcolor={allSelected ? null : '#fff'}
                              color={allSelected ? null : Colors.light.hereworksBlue100}
                              style={{ marginStart: 26, marginEnd: 14 }}
                            />
                          </Pressable>
                        )}
                        <View style={{ flexDirection: 'row', flex: 1 }}>
                          <View style={styles?.sortContainer}>
                            <Pressable
                              onPress={() => {
                                logEvent('AssetMgmtScreenWeb', 'sort_by_id');
                                hideMenus();
                                setSortById(!sortById);
                                setSortByFloor(null);
                                setSortByArea(null);
                              }}
                              style={(state) => [styles.sortButton, {
                                backgroundColor:
                                  sortById !== null ? Colors.light.hereworksWhite100 : '#fff',
                                borderColor:
                                  sortById !== null ? Colors.light.hereworksWhite500 : '#fff',
                              }, state.hovered && Style.hovered, state.pressed && Style.pressed]}
                            >
                              <OrionArrowIcon
                                style={{ transform: [{ rotateZ: sortById ? '90deg' : '270deg' }] }}
                              />
                              <Text style={styles.headerText}>ID</Text>
                            </Pressable>
                          </View>
                          <View style={styles?.sortContainer}>
                            <Pressable
                              onPress={() => {
                                logEvent('AssetMgmtScreenWeb', 'sort_by_floor');
                                hideMenus();
                                setSortById(null);
                                setSortByFloor(!sortByFloor);
                                setSortByArea(null);
                              }}
                              style={(state) => [styles.sortButton, {
                                backgroundColor:
                                  sortByFloor !== null ? Colors.light.hereworksWhite100 : '#fff',
                                borderColor:
                                  sortByFloor !== null ? Colors.light.hereworksWhite500 : '#fff',
                              }, state.hovered && Style.hovered, state.pressed && Style.pressed]}
                            >
                              <OrionArrowIcon
                                style={{ transform: [{ rotateZ: sortByFloor ? '90deg' : '270deg' }] }}
                              />
                              <Text style={styles.headerText}>FLOOR</Text>
                            </Pressable>
                          </View>
                          <View style={styles?.sortContainer}>
                            <Pressable
                              onPress={() => {
                                hideMenus();
                                setSortById(null);
                                setSortByFloor(null);
                                setSortByArea(!sortByArea);
                              }}
                              style={(state) => [styles.sortButton, {
                                backgroundColor:
                                  sortByArea !== null ? Colors.light.hereworksWhite100 : '#fff',
                                borderColor:
                                  sortByArea !== null ? Colors.light.hereworksWhite500 : '#fff',
                              }, state.hovered && Style.hovered, state.pressed && Style.pressed]}
                            >
                              <OrionArrowIcon
                                style={{ transform: [{ rotateZ: sortByArea ? '90deg' : '270deg' }] }}
                              />
                              <Text style={styles.headerText}>AREA</Text>
                            </Pressable>
                          </View>
                        </View>
                        <View>
                          <Text
                            style={[
                              styles.headerText,
                              {
                                flex: 1,
                                width: 96,
                                alignSelf: 'flex-end',
                                marginEnd: 12,
                              },
                            ]}
                          >
                            ACTIONS
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>
                )}
                <ScrollView style={{ paddingHorizontal: 24, paddingBottom: 90 }}>
                  <View style={[styles.divider, { marginBottom: 24 }]} />
                  {assetsLoading
                    && <View style={{ flex: 1 }}>
                      <View style={{ flex: 1, justifyContent: 'center' }}>
                        <ActivityIndicator size={'large'} color={Colors.light.hereworksLoading} />
                      </View>
                    </View>
                  }
                  {!selectedTemplate && !(assetFilterResults?.length > 0) && <View
                    style={{
                      backgroundColor: Colors.light.hereworksWhite100,
                      borderRadius: 8,
                      margin: 8,
                      paddingVertical: 8,
                      paddingHorizontal: 16,
                    }}>
                    <Text style={{ fontFamily: 'Poppins_500Medium', fontSize: 14, color: Colors.light.hereworksBlack600 }}>
                      {'No template selected'}
                    </Text>
                    <Text style={{ fontFamily: 'Roboto_400Regular', fontSize: 12, color: Colors.light.hereworksBlack400 }}>
                      {'Please select a template from the list'}
                    </Text>
                  </View>}
                  {(selectedTemplate && !(filterAssets().length > 0) && !assetsLoading
                    && !(assetFilterResults.length > 0)) && <ScreenEmptyState
                      image={<NoAssetsImage />}
                      title={selectedBuildingId ? 'Add asset in template'
                        : 'No buildings available'
                      }
                      subtitle={selectedBuildingId
                        ? 'Add an asset to the current template\n(Assets can only be added via the mobile app)'
                        : 'There are no buildings in the system. A building is required for adding assets.'}
                      buttonTitle=''
                      buttonAction={() => { }}
                      actionAvailable
                    />}
                  {selectedAsset ? (
                    renderItem({ item: selectedAsset }, true)
                  ) : (selectedTemplate || assetFilterResults?.length > 0) && (
                    <FlatList
                      testID='assets-list'
                      refreshControl={
                        <RefreshControl refreshing={assetFilterLoading} onRefresh={refresh} />
                      }
                      data={assetFilterResults.length > 0 ? assetFilterResults : filterAssets()}
                      {...{ renderItem }}
                    />
                  )}
                </ScrollView>
              </View>
            </View>
            <AssetFilter
              {...{
                clearAssetFilter,
                logEvent,
                assetFilterLoading,
                setAssetFilterLoading,
                assetFilterResults,
                detailedFilterVisible,
                setDetailedFilterVisible,
                setAssetFilterResults,
              }}
            />
          </Animatable.View>
        )}
        <AlertModal
          modalVisible={alertVisible}
          title={alertTitle}
          message={alertMessage}
          error={alertError}
          setModalVisible={setAlertVisible}
          actionTitle={userGroups.length ? '' : 'Go to user management'}
          action={userGroups.length ? null : () => {
            navigation.navigate(AppScreen.USER_MANAGEMENT);
          }}
          isMessage={!userGroups.length}
        />
        <AddTemplateModal
          templateGroups={templateGroups}
          isVisible={editModalVisible}
          hide={() => setEditModalVisible(false)}
          templateToUpdate={selectedTemplate}
          update={updateTemplateLocally}
          showParentAlert={showScreenAlert}
        />
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 40,
    backgroundColor: Colors.light.hereworksBackground,
    flex: 1,
    height: Dimensions.get('screen').height - 120,
  },
  sectionTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    marginStart: 24,
    marginEnd: 50,
    marginVertical: 16,
  },
  selectedTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 18,
    lineHeight: 27,
    color: Colors.light.hereworksBlack600,
  },
  headingSubTitle: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 11,
    lineHeight: 13,
    marginVertical: 4,
  },
  headingValue: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 14,
    lineHeight: 21,
    color: Colors.light.hereworksBlack600,
  },
  divider: {
    height: 1,
    backgroundColor: Colors.light.hereworksWhite500,
    width: '100%',
    marginTop: 0,
  },
  createButton: {
    borderRadius: 4,
    backgroundColor: Colors.light.hereworksBlue500,
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 20,
    marginStart: 40,
  },
  createButtonTitle: {
    color: '#fff',
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    marginStart: 24,
    marginEnd: 10,
    marginVertical: 12,
  },
  searchBox: {
    height: 40,
    flex: 1,
    borderRadius: 24,
    backgroundColor: '#fff',
    borderColor: Colors.light.hereworksPaleBlue900,
    borderWidth: 1.5,
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchText: {
    flex: 1,
    color: Colors.light.hereworksBlack600,
    marginStart: 9,
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 16.4,
    outlineWidth: 0,
  },
  headerText: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 10,
    lineHeight: 15,
    letterSpacing: 0.7,
    marginEnd: 8,
    color: Colors.light.hereworksBlack400,
  },
  sortContainer: {
    flex: 1,
    alignItems: 'flex-start',
    marginStart: 30,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    paddingHorizontal: 24,
    alignSelf: 'flex-start',
    marginTop: 20,
  },
  filterButtonTitle: {
    fontSize: 14,
    lineHeight: 22,
    marginVertical: 5,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlue500,
    marginEnd: 8,
  },
  contentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    height: 62,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue100,
  },
  backButton: {
    marginEnd: 12,
    width: 64,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    borderRadius: 4,
  },
  contentHeaderButtonSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginEnd: 40,
  },
  contentHeaderText: {
    fontSize: 12,
    lineHeight: 20,
    color: Colors.light.hereworksBlack500,
    fontFamily: 'Roboto_500Medium',
  },
  tabSelectionHighlight: {
    backgroundColor: Colors.light.hereworksBlack600,
    height: 2,
    marginTop: 4,
  },
  noTemplatesSubtitle: {
    fontSize: 12,
    lineHeight: 18,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_400Regular',
  },
  noTemplateGroupsMessage: {
    borderRadius: 8,
    backgroundColor: Colors.light.hereworksWhite100,
    paddingHorizontal: 12,
    paddingVertical: 16,
    margin: 16,
  },
  addTemplateGroupsButton: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderRadius: 4,
    borderColor: Colors.light.hereworksBlue500,
    padding: 4,
    marginEnd: 18,
  },
  templateGroupsTitle: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    lineHeight: 20,
    color: Colors.light.hereworksBlack500,
    marginTop: 24,
    marginStart: 24,
    marginBottom: 17,
  },
  exportDropdown: {
    position: 'absolute',
    top: 48,
    end: 24,
    backgroundColor: '#FFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue200,
  },
  exportOptionText: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  sortButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 4,
  },
});
